/**
 * Route-level error and not-found states (loading states live in `route-pending.tsx`).
 *
 * Error and not-found screens come in two layouts: `page` stands on its own
 * (no shell rendered — unknown top-level URLs, a failed organization load) and
 * `inline` sits inside the app shell's content column. Both share one raised
 * status card so every dead end looks the same and offers a way out.
 */
import { isApiClientError } from "@nudgebox/api-client";
import { Button, CopyButton, Heading, Logo, Stack, surface, Text, useIsMobile } from "@nudgebox/ui";
import { AlertIcon, ArrowLeftIcon, CompassIcon, HomeIcon, LockIcon, OfflineIcon, RetryIcon } from "@nudgebox/ui/icons";
import "@nudgebox/ui/tokens.stylex";
import { color, font, fontSize, media, radius, space, texture } from "@nudgebox/ui/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { type ErrorComponentProps, Link, useCanGoBack, useLocation, useParams, useRouter } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";
import { errorMessage } from "@/lib/errors.ts";
type Layout = "page" | "inline";
type Tone = "neutral" | "error";

/* ------------------------------------------------------------------------ */
/* Not found                                                                */
/* ------------------------------------------------------------------------ */

/** Unknown URL inside an organization; rendered in the shell. */
export function RouteNotFound() {
  return <NotFound layout="inline" />;
}

/** Unknown URL anywhere else; rendered as a standalone page. */
export function PageNotFound() {
  return <NotFound layout="page" />;
}
function NotFound({
  layout
}: {
  layout: Layout;
}) {
  const {
    pathname
  } = useLocation();
  return <StatusScreen layout={layout} icon={<CompassIcon size={22} />} eyebrow="404 · Not found" title="We couldn't find that page" description="The link may be broken, the page may have moved, or you might not have access to it. Check the URL or head somewhere familiar." actions={<>
          <HomeButton layout={layout} variant="primary" />
          <BackButton />
        </>} details={[{
    label: "Path",
    value: pathname,
    copyable: false
  }]} />;
}

/* ------------------------------------------------------------------------ */
/* Errors                                                                   */
/* ------------------------------------------------------------------------ */

/** A route below the shell failed; rendered in the shell. */
export function RouteError(props: ErrorComponentProps) {
  return <ErrorScreen {...props} layout="inline" />;
}

/** The shell itself (or anything above it) failed; rendered as a standalone page. */
export function PageError(props: ErrorComponentProps) {
  return <ErrorScreen {...props} layout="page" />;
}
function ErrorScreen({
  error,
  layout
}: ErrorComponentProps & {
  layout: Layout;
}) {
  const router = useRouter();
  const {
    pathname,
    href
  } = useLocation();
  const queryErrorResetBoundary = useQueryErrorResetBoundary();

  // Let suspense queries refetch when the route re-renders after a retry.
  useEffect(() => {
    queryErrorResetBoundary.reset();
  }, [queryErrorResetBoundary]);
  if (isApiClientError(error) && error.status === 404) return <NotFound layout={layout} />;

  // `invalidate` re-runs loaders and resets the route's error boundary.
  const retry = <Button variant="primary" icon={<RetryIcon size={14} />} onClick={() => void router.invalidate()}>
      Try again
    </Button>;
  const api = isApiClientError(error) ? error : null;
  const requestId = api?.requestId;
  const details: Detail[] = [...(api && api.status > 0 ? [{
    label: "Status",
    value: `${api.status} ${api.code}`,
    copyable: false
  }] : []), ...(requestId ? [{
    label: "Request",
    value: requestId,
    copyable: true
  }] : []),
  // Unexpected (non-API) errors get a friendly description; keep the raw message for bug reports.
  ...(!api ? [{
    label: "Error",
    value: errorMessage(error),
    copyable: true
  }] : []), {
    label: "Path",
    value: pathname,
    copyable: false
  }];

  // Offline, the session lookup fails before the API client sees anything (e.g. the installed app
  // launched from the service worker's cached shell), so trust the browser's own signal too.
  if (api?.code === "network_error" || !navigator.onLine) {
    return <StatusScreen layout={layout} icon={<OfflineIcon size={22} />} eyebrow="Network error" title="Can't reach Nudgebox" description="Your connection dropped or our servers couldn't be reached. Check your network and try again — nothing you've saved is lost." actions={retry} details={details} />;
  }
  if (api?.code === "unauthorized") {
    return <StatusScreen layout={layout} icon={<LockIcon size={22} />} eyebrow="401 · Signed out" title="Your session has expired" description="Sign in again to pick up where you left off." actions={<Button variant="primary" render={<Link to="/sign-in" search={{
      redirect: href
    }} />} nativeButton={false}>
            Sign in
          </Button>} />;
  }
  if (api?.code === "forbidden") {
    return <StatusScreen layout={layout} icon={<LockIcon size={22} />} eyebrow="403 · No access" title="You don't have access to this" description="Your role in this organization doesn't allow this. Ask an owner to change your role, or head back to somewhere you can work." actions={<>
            <HomeButton layout={layout} variant="primary" />
            <BackButton />
          </>} details={details} />;
  }
  return <StatusScreen layout={layout} tone="error" icon={<AlertIcon size={22} />} eyebrow={api && api.status > 0 ? `${api.status} · Error` : "Unexpected error"} title="This page couldn't load" description={api ? api.message : "Something broke on our side while loading this page. Trying again usually fixes it; if it keeps happening, send us the details below."} actions={<>
          {retry}
          <HomeButton layout={layout} />
        </>} details={details} />;
}

/* ------------------------------------------------------------------------ */
/* Actions                                                                  */
/* ------------------------------------------------------------------------ */

/**
 * Inside the shell, "home" is the organization's overview. A standalone page
 * may be standing in for an organization that failed to load (or doesn't
 * exist), so it goes to the root redirect instead.
 */
function HomeButton({
  layout,
  variant = "secondary"
}: {
  layout: Layout;
  variant?: "primary" | "secondary";
}) {
  const {
    orgSlug
  } = useParams({
    strict: false
  });
  return layout === "inline" && orgSlug ? <Button variant={variant} icon={<HomeIcon size={14} />} render={<Link to="/$orgSlug" params={{
    orgSlug
  }} />} nativeButton={false}>
      Back to overview
    </Button> : <Button variant={variant} icon={<HomeIcon size={14} />} render={<Link to="/" />} nativeButton={false}>
      Go home
    </Button>;
}
function BackButton() {
  const router = useRouter();
  const canGoBack = useCanGoBack();
  if (!canGoBack) return null;
  return <Button variant="ghost" icon={<ArrowLeftIcon size={14} />} onClick={() => router.history.back()}>
      Go back
    </Button>;
}

/* ------------------------------------------------------------------------ */
/* Status card                                                              */
/* ------------------------------------------------------------------------ */

interface Detail {
  label: string;
  value: string;
  copyable: boolean;
}
function StatusScreen({
  layout,
  tone = "neutral",
  icon,
  eyebrow,
  title,
  description,
  actions,
  details = []
}: {
  layout: Layout;
  tone?: Tone;
  icon: ReactNode;
  eyebrow: string;
  title: string;
  description: ReactNode;
  actions: ReactNode;
  details?: Detail[];
}) {
  const isMobile = useIsMobile();
  const card = <section aria-labelledby="status-title" {...stylex.props(surface.raised, surface.lg, styles.card, layout === "inline" && styles.cardInline)}>
      <div className="x78zum5 xdt5ytf x6s0dn4 x9p7km9 x7xzu7j x14yo7wi xdf0l85 xme3qm3 x1c0oshy">
        <div {...stylex.props(surface.raised, surface.sm, styles.icon, tone === "error" && styles.iconError)}>
          {icon}
        </div>
        <Stack gap="sm" align="center">
          <Text size="xs" tone={tone === "error" ? "error" : "tertiary"} mono>
            {eyebrow}
          </Text>
          <Heading level={1} id="status-title">
            {title}
          </Heading>
          <Text tone="secondary" as="p">
            {description}
          </Text>
        </Stack>
        <div {...{
        0: {
          className: "x78zum5 x1a02dak xl56j7k x1a62zme"
        },
        1: {
          className: "x78zum5 x1a02dak xl56j7k x1a62zme xdt5ytf xkh2ocl"
        }
      }[!!isMobile << 0]}>{actions}</div>
      </div>

      {details.length ? <dl className="x1ghz6dp x1imsgqj xh1le5o x1lavhwh x178xt8z xlya59e x1fwp53n xdpxx8g">
          {details.map(detail => <div key={detail.label} className="x78zum5 x6s0dn4 xpcbtbh x21xpn4">
              <dt className="x2lah0s x1fu8urw xmccgnw xxe3v3u">{detail.label}</dt>
              <dd className="x1iyjqo2 xeuugli x1ghz6dp xb3r6kr xlyipyv xuxw1ft xxg9qwl xmccgnw x187ovcu" title={detail.value}>
                {detail.value}
              </dd>
              {detail.copyable ? <CopyButton value={detail.value} label={`Copy ${detail.label.toLowerCase()}`} compact variant="ghost" /> : null}
            </div>)}
        </dl> : null}
    </section>;
  if (layout === "inline") return <div className="x78zum5 xl56j7k xlg37eu xcf4dyh">{card}</div>;
  return <main className="x1ov3xa9 x78zum5 xdt5ytf x6s0dn4 xl56j7k xxyh0xd x12qnzkd xws8vp0 xvccuex x1c9kulo xxmn2u8">
      <Link to="/" aria-label="Nudgebox home" className="x78zum5 x1hl2dhg xoazux2">
        <Logo />
      </Link>
      {card}
    </main>;
}
const styles = {
  card: {
    kzqmXN: "xh8yej3",
    ks0D6T: "x1pfbilc",
    kaIpWk: "x16l8lxw",
    k9WMMc: "x2b8uid",
    $$css: true
  },
  cardInline: {
    ks0D6T: "x1lwm3bp",
    $$css: true
  },
  icon: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kzqmXN: "x1useyqa",
    kZKoxP: "xsdox4t",
    kaIpWk: "xlfxrz8",
    kMwMTN: "x187ovcu",
    $$css: true
  },
  iconError: {
    "--face-bg": "x18d8edl",
    "--face-border": "x1raya8z",
    "--face-dot": "x1y7s6vt",
    kMwMTN: "x1xcodwn",
    $$css: true
  }
};